import { useState } from "react";
import { Link, useLocation } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import {
    LayoutDashboard,
    FileText,
    CheckSquare,
    Settings,
    PanelLeft,
    LogOut,
    ChevronRight,
    FileStack,
    BookUser,
    DollarSign,
    Zap
} from "lucide-react";
import { cn } from "@/lib/utils";
import logo from "@/assets/turboclaim-logo.png";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import {
    Sheet,
    SheetContent,
    SheetTitle,
} from "@/components/ui/sheet";

interface SidebarBodyProps {
    /** Whether the (desktop) sidebar is collapsed to an icon rail. */
    isCollapsed?: boolean;
    /** Optional toggle control rendered in the header when provided. */
    onToggle?: () => void;
    /** Called after a navigation link is clicked (used to close the mobile drawer). */
    onNavigate?: () => void;
}

/**
 * Shared sidebar content used by both the desktop rail and the mobile drawer.
 */
function SidebarBody({ isCollapsed = false, onToggle, onNavigate }: SidebarBodyProps) {
    const [location, setLocation] = useLocation();
    const { user, logoutMutation } = useAuth();

    const handleLogout = () => {
        logoutMutation.mutate();
        setLocation("/login");
    };

    const menuItems = [
        {
            title: "My Workspace",
            url: "/",
            icon: LayoutDashboard,
        },
        {
            title: "Claims",
            url: "/claims",
            icon: FileText,
        },
        {
            title: "Directory",
            url: "/directory",
            icon: BookUser,
        },
        {
            title: "Tasks",
            url: "/tasks",
            icon: CheckSquare,
        },
        {
            title: "Templates",
            url: "/templates",
            icon: FileStack,
        },
        ...(user?.role === 'admin' || user?.role === 'owner'
            ? [
                  {
                      title: "Automations",
                      url: "/automations",
                      icon: Zap,
                  },
              ]
            : []),
        {
            title: "Financials",
            url: "/financials",
            icon: DollarSign,
        },
        {
            title: "Settings",
            url: "/settings",
            icon: Settings,
        },
    ];

    return (
        <TooltipProvider>
            {/* Header / Logo */}
            <div className="h-16 flex items-center justify-between px-4 border-b border-sidebar-border shrink-0">
                <AnimatePresence mode="wait">
                    {!isCollapsed && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.1 }}
                            className="flex-1"
                        >
                            <Link href="/" onClick={onNavigate}>
                                <img src={logo} alt="TurboClaim" className="h-8 object-contain cursor-pointer" />
                            </Link>
                        </motion.div>
                    )}
                </AnimatePresence>
                {onToggle && (
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={onToggle}
                        className={cn("shrink-0", isCollapsed ? "mx-auto" : "ml-2")}
                    >
                        <PanelLeft className="h-5 w-5 text-muted-foreground" />
                    </Button>
                )}
            </div>

            {/* Navigation Links */}
            <div className="flex-1 py-4 px-2 space-y-1 overflow-y-auto">
                {menuItems.map((item) => {
                    const isActive = location === item.url || (item.url !== "/" && location.startsWith(item.url));

                    return (
                        <Tooltip key={item.url} delayDuration={0}>
                            <TooltipTrigger asChild>
                                <Link href={item.url} onClick={onNavigate}>
                                    <div
                                        className={cn(
                                            "flex items-center gap-3 px-3 py-2 rounded-lg transition-colors duration-100 cursor-pointer group",
                                            isActive
                                                ? "bg-sidebar-accent text-foreground font-medium"
                                                : "text-muted-foreground hover:bg-sidebar-accent hover:text-foreground"
                                        )}
                                    >
                                        <item.icon className={cn("h-5 w-5 shrink-0", isActive ? "text-primary" : "text-muted-foreground group-hover:text-foreground")} />
                                        {!isCollapsed && (
                                            <motion.span
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                transition={{ duration: 0.1 }}
                                                className="truncate"
                                            >
                                                {item.title}
                                            </motion.span>
                                        )}
                                    </div>
                                </Link>
                            </TooltipTrigger>
                            {isCollapsed && (
                                <TooltipContent side="right">
                                    {item.title}
                                </TooltipContent>
                            )}
                        </Tooltip>
                    );
                })}
            </div>

            {/* Footer / User Profile */}
            <div className="p-2 border-t border-sidebar-border mt-auto shrink-0">
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button
                            variant="ghost"
                            className={cn(
                                "w-full flex items-center gap-2 px-2 h-auto py-2 hover:bg-sidebar-accent hover:text-foreground",
                                isCollapsed ? "justify-center" : "justify-start"
                            )}
                        >
                            <Avatar className="h-8 w-8 border border-border">
                                <AvatarImage />
                                <AvatarFallback className="rounded-lg">{user?.name?.[0] || 'U'}</AvatarFallback>
                            </Avatar>
                            {!isCollapsed && (
                                <>
                                    <div className="flex flex-col items-start overflow-hidden ml-1">
                                        <span className="text-sm font-medium truncate w-32 text-left text-foreground">
                                            {user?.name || 'User'}
                                        </span>
                                        <span className="text-xs text-muted-foreground truncate w-32 text-left">
                                            {user?.email}
                                        </span>
                                    </div>
                                    <ChevronRight className="h-4 w-4 ml-auto text-muted-foreground" />
                                </>
                            )}
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                        align="start"
                        side={isCollapsed ? "right" : "top"}
                        sideOffset={8}
                        className="w-56"
                    >
                        <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive cursor-pointer">
                            <LogOut className="mr-2 h-4 w-4" />
                            Log Out
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </TooltipProvider>
    );
}

/**
 * Desktop sidebar rail. Hidden on mobile viewports where {@link MobileSidebar} is used instead.
 */
export function Sidebar() {
    const [isCollapsed, setIsCollapsed] = useState(false);

    const toggleSidebar = () => setIsCollapsed(!isCollapsed);

    return (
        <motion.div
            initial={false}
            animate={{ width: isCollapsed ? "4rem" : "16rem" }}
            transition={{ duration: 0.1 }}
            className={cn(
                "relative hidden md:flex flex-col border-r border-sidebar-border bg-sidebar h-screen z-20",
                isCollapsed ? "w-16" : "w-64"
            )}
        >
            <SidebarBody isCollapsed={isCollapsed} onToggle={toggleSidebar} />
        </motion.div>
    );
}

interface MobileSidebarProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

/**
 * Mobile navigation drawer, shown as a slide-out sheet on small viewports.
 */
export function MobileSidebar({ open, onOpenChange }: MobileSidebarProps) {
    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="left"
                className="w-64 max-w-[80vw] p-0 flex flex-col bg-sidebar border-sidebar-border md:hidden"
            >
                <SheetTitle className="sr-only">Navigation</SheetTitle>
                <SidebarBody onNavigate={() => onOpenChange(false)} />
            </SheetContent>
        </Sheet>
    );
}
